import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-[#F8F7F4]" data-testid="terms-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#4B6651] hover:underline mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to App
        </Link>

        <h1 className="text-2xl sm:text-3xl font-semibold text-[#2A2C2A] font-['Work_Sans'] mb-2">
          Terms of Service
        </h1>
        <p className="text-sm text-[#5C5F5C] mb-8">Last updated: March 28, 2026</p>

        <div className="prose prose-sm max-w-none space-y-6 text-[#2A2C2A]">
          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">1. Acceptance of Terms</h2>
            <p className="text-[#5C5F5C] leading-relaxed mt-3">
              By accessing or using VetNeuroHelp ("the Service"), operated by VetNeuroHelp, Ellijay, GA ("we," "our," or "us"), you agree to be bound by these Terms of Service. If you do not agree, do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">2. Description of Service</h2>
            <p className="text-[#5C5F5C] leading-relaxed mt-3">
              VetNeuroHelp is an AI-powered veterinary neurological diagnostic tool designed to assist licensed veterinarians in localizing neurological lesions in cats and dogs, generating differential diagnosis lists, and creating diagnostic test plans. The Service is intended as a clinical decision support tool and does not replace professional veterinary judgment.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">3. Account Registration</h2>
            <p className="text-[#5C5F5C] leading-relaxed mt-3">
              To use the Service, you must register an account and provide accurate information, including a valid veterinary license number and issuing entity. You are responsible for maintaining the confidentiality of your account credentials. You must notify us immediately of any unauthorized use of your account.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">4. Subscription & Billing</h2>
            <p className="text-[#5C5F5C] leading-relaxed mt-3">
              New accounts receive a 14-day free trial. After the trial period, continued access to premium features requires a paid subscription. Subscription fees are billed monthly through Stripe. You may upgrade, downgrade, or cancel your subscription at any time. Cancellations take effect at the end of the current billing period. Refunds are handled on a case-by-case basis.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">5. Acceptable Use</h2>
            <p className="text-[#5C5F5C] leading-relaxed mt-3">You agree not to:</p>
            <ul className="list-disc pl-6 text-[#5C5F5C] leading-relaxed space-y-1 mt-2">
              <li>Use the Service for any unlawful purpose</li>
              <li>Misrepresent your professional credentials or veterinary license</li>
              <li>Share your account credentials with unauthorized users</li>
              <li>Attempt to reverse-engineer, decompile, or disassemble the Service</li>
              <li>Upload malicious content, viruses, or harmful code</li>
              <li>Use the Service to provide veterinary care beyond your scope of licensure</li>
              <li>Scrape, harvest, or collect data from the Service by automated means</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">6. Medical Disclaimer</h2>
            <p className="text-[#5C5F5C] leading-relaxed mt-3">
              <strong className="text-[#C06144]">The Service is a clinical decision support tool only.</strong> AI-generated analyses, differential diagnoses, and diagnostic plans are not a substitute for professional veterinary judgment. All clinical decisions remain the sole responsibility of the licensed veterinarian. VetNeuroHelp does not guarantee the accuracy, completeness, or reliability of AI-generated content. Always verify AI recommendations with your clinical expertise and current veterinary literature.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">7. Intellectual Property</h2>
            <p className="text-[#5C5F5C] leading-relaxed mt-3">
              All content, features, and functionality of the Service are owned by VetNeuroHelp and are protected by copyright, trademark, and other intellectual property laws. You retain ownership of patient data and clinical records you input into the Service.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">8. Third-Party Services</h2>
            <p className="text-[#5C5F5C] leading-relaxed mt-3">
              The Service integrates with third-party providers. By using VetNeuroHelp, you also agree to comply with the terms and policies of:
            </p>
            <ul className="list-disc pl-6 text-[#5C5F5C] leading-relaxed space-y-1 mt-2">
              <li><strong>OpenAI</strong> — AI analysis is powered by OpenAI's GPT models. Usage is subject to <a href="https://openai.com/policies/terms-of-use" className="text-[#4B6651] hover:underline" target="_blank" rel="noopener noreferrer">OpenAI's Terms of Use</a> and <a href="https://openai.com/policies/usage-policies" className="text-[#4B6651] hover:underline" target="_blank" rel="noopener noreferrer">Usage Policies</a>.</li>
              <li><strong>Stripe</strong> — Payment processing is handled by Stripe. Transactions are subject to <a href="https://stripe.com/legal" className="text-[#4B6651] hover:underline" target="_blank" rel="noopener noreferrer">Stripe's Services Agreement</a>.</li>
              <li><strong>SendGrid</strong> — Email delivery is provided by Twilio SendGrid, subject to <a href="https://www.twilio.com/legal/tos" className="text-[#4B6651] hover:underline" target="_blank" rel="noopener noreferrer">Twilio's Terms of Service</a>.</li>
            </ul>
            <p className="text-[#5C5F5C] leading-relaxed mt-2">
              We are not responsible for the availability, accuracy, or practices of these third-party services.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">9. Limitation of Liability</h2>
            <p className="text-[#5C5F5C] leading-relaxed mt-3">
              To the fullest extent permitted by law, VetNeuroHelp shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or goodwill, arising from your use of the Service. Our total liability shall not exceed the amount you paid for the Service in the 12 months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">10. Termination</h2>
            <p className="text-[#5C5F5C] leading-relaxed mt-3">
              We may terminate or suspend your account at any time for violation of these Terms. You may delete your account at any time by contacting us. Upon termination, your right to use the Service ceases immediately. We may retain anonymized, aggregated data for analytical purposes.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">11. Governing Law</h2>
            <p className="text-[#5C5F5C] leading-relaxed mt-3">
              These Terms shall be governed by and construed in accordance with the laws of the State of Georgia, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be resolved in the courts of Gilmer County, Georgia.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">12. Changes to Terms</h2>
            <p className="text-[#5C5F5C] leading-relaxed mt-3">
              We reserve the right to modify these Terms at any time. Changes will be posted on this page with an updated revision date. Continued use of the Service after changes are posted constitutes acceptance of the revised Terms.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">13. Contact</h2>
            <p className="text-[#5C5F5C] leading-relaxed mt-3">
              For questions about these Terms, contact us at:<br />
              <strong>VetNeuroHelp</strong><br />
              Ellijay, GA<br />
              <a href="mailto:helpme@vetneurohelp.com" className="text-[#4B6651] hover:underline">helpme@vetneurohelp.com</a>
            </p>
          </section>
        </div>

        <div className="mt-12 pt-6 pb-12 border-t border-[#E5E4E0] flex flex-wrap gap-4 text-sm text-[#5C5F5C]">
          <Link to="/privacy" className="hover:text-[#4B6651] hover:underline">Privacy Policy</Link>
          <Link to="/contact" className="hover:text-[#4B6651] hover:underline">Contact Us</Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
